// src/sim/active-effects.ts -- pure, no DOM
// Time-based active effects (napalm flow, liquid dirt).

import type { Terrain } from './terrain';
import { applyShieldedDamage } from './shields';

export interface ActiveEffect {
  type: 'napalm' | 'liquid-dirt';
  x: number;
  y: number;
  direction: number;  // -1 or 1
  flowRate: number;
  damagePerTick: number;
  remainingTime: number;
  owner: number;
  tickAccum: number;  // accumulator for damage ticks
}

export function createNapalmEffect(
  x: number,
  y: number,
  direction: number,
  flowRate: number,
  damagePerTick: number,
  owner: number,
): ActiveEffect {
  return {
    type: 'napalm',
    x, y, direction, flowRate, damagePerTick,
    remainingTime: 2.5,
    owner,
    tickAccum: 0,
  };
}

export function createLiquidDirtEffect(
  x: number,
  y: number,
  direction: number,
  flowRate: number,
  owner: number,
): ActiveEffect {
  return {
    type: 'liquid-dirt',
    x, y, direction, flowRate,
    damagePerTick: 0,
    remainingTime: 2.5,
    owner,
    tickAccum: 0,
  };
}

/**
 * Step all active effects. Returns true if any effects still active.
 * Mutates the effects array (removes completed effects).
 */
export function stepEffects(
  effects: ActiveEffect[],
  terrain: Terrain,
  tanks: Array<{ x: number; y: number; hp: number; alive: boolean; flashTimer: number; activeShield: { defId: string; energy: number } | null }>,
  dt: number,
  worldWidth: number,
): boolean {
  for (let i = effects.length - 1; i >= 0; i--) {
    const e = effects[i];
    e.remainingTime -= dt;

    // Hard cap at 5 seconds
    if (e.remainingTime <= 0 || e.remainingTime < -2.5) {
      effects.splice(i, 1);
      continue;
    }

    // Move along terrain surface
    e.x += e.direction * e.flowRate * dt;

    // Bounds check
    if (e.x < 0 || e.x >= worldWidth) {
      effects.splice(i, 1);
      continue;
    }

    // Follow terrain surface
    const surfY = terrain.surfaceY(Math.round(e.x));
    e.y = surfY - 1;

    // Check if going uphill and slow/reverse
    const aheadX = Math.round(e.x + e.direction * 3);
    if (aheadX >= 0 && aheadX < worldWidth) {
      const aheadY = terrain.surfaceY(aheadX);
      if (aheadY < surfY - 3) {
        // Uphill -- reverse direction
        e.direction = -e.direction;
      }
    }

    if (e.type === 'napalm') {
      // Carve small terrain at current position
      terrain.carve(e.x, e.y, 3);

      // Damage tanks in contact
      e.tickAccum += dt;
      if (e.tickAccum >= 0.2) {
        e.tickAccum -= 0.2;
        for (const tank of tanks) {
          if (!tank.alive) continue;
          if (Math.abs(tank.x - e.x) < 12 && Math.abs(tank.y - e.y) < 12) {
            applyShieldedDamage(tank, e.damagePerTick, false);
          }
        }
      }
    } else if (e.type === 'liquid-dirt') {
      // Deposit dirt at current position
      terrain.addDirt(Math.round(e.x), Math.round(e.y), 4);
    }
  }

  return effects.length > 0;
}
